import {translate} from 'views/module';

import {describeWorkflow, humanizeKind} from 'views/scenes/loom_lead/detail/client_scenes/client_feed/workflows/workflow_status';
import {formatDateTime, translateOr} from 'views/scenes/loom_lead/detail/client_scenes/client_feed/format';

// Turns a job invitation workflow into what its card shows. Loom only stores the steady state that
// Mastra last reported, so the stepper is the invitation's three stages.
export const JOB_INVITATION_STEPS = ['InvitationSent', 'ContactingCaregiver', 'Finished'];

// A closed job invitation ends with its invitation's final status.
const FINISHED = {
  COMPLETED: {state: 'COMPLETED', tone: 'success', finishedStep: 'InterviewCompleted'},
  DECLINED: {state: 'DECLINED', tone: 'neutral', finishedStep: 'Declined'},
  UNREACHABLE: {state: 'UNREACHABLE', tone: 'neutral', finishedStep: 'Unreachable'},
  CANCELLED: {state: 'CANCELLED', tone: 'neutral', finishedStep: 'Cancelled'},
};

const describeState = (workflow) => {
  if (workflow.steadyState === 'CLOSED' || workflow.status !== 'ACTIVE') {
    return {
      state: 'CLOSED',
      tone: 'neutral',
      finishedStep: 'Closed',
      ...FINISHED[workflow.outcome || workflow.jobInvitation?.invitationStatus],
      activeStep: JOB_INVITATION_STEPS.length,
    };
  }

  if (!workflow.engineRunBound) {
    return {state: 'NOT_STARTED', activeStep: 0, tone: 'error'};
  }

  if (!workflow.steadyState) {
    return {state: 'STARTING', activeStep: 0, tone: 'info'};
  }

  if (workflow.steadyState === 'AWAITING_REPLY') {
    return {state: 'AWAITING_CAREGIVER', activeStep: 1, tone: 'info'};
  }

  if (workflow.steadyState === 'CALL_BOOKED') {
    return {state: 'CALL_BOOKED', activeStep: 1, tone: 'info'};
  }

  if (workflow.steadyState === 'IN_REVIEW') {
    return {state: 'TEAM_REVIEW', activeStep: 1, tone: 'warning'};
  }

  return {state: 'IN_PROGRESS', activeStep: 1, tone: 'info'};
};

// The attention rules (no engine run, a failed escalation) are the same for every workflow type.
export const describeJobInvitation = (workflow) => ({
  ...describeState(workflow),
  attention: describeWorkflow(workflow).attention,
});

export const jobInvitationDetails = (workflow) => {
  const invitation = workflow.jobInvitation;
  const job = invitation?.job;
  const status = invitation?.invitationStatus;

  return [
    {
      key: 'job',
      label: translate('caregiver.LoomCaregiverDetail.Workflows.Details.Job'),
      value: job?.summary,
    },
    {
      key: 'city',
      label: translate('caregiver.LoomCaregiverDetail.Workflows.Details.City'),
      value: job?.city,
    },
    {
      key: 'days',
      label: translate('caregiver.LoomCaregiverDetail.Workflows.Details.Days'),
      value: (job?.days || []).map((day) => translateOr(`job.ClientJobs.Days.${day}`, humanizeKind(day))).join(', '),
    },
    {
      key: 'invitation',
      label: translate('caregiver.LoomCaregiverDetail.Workflows.Details.Invitation'),
      value: status && translateOr(`career.jobDetail.Invitations.Status.${status}`, humanizeKind(status)),
    },
    {
      key: 'followUps',
      label: translate('caregiver.LoomCaregiverDetail.Workflows.Details.FollowUps'),
      value: invitation ? String(invitation.followUpRoundsSent) : null,
    },
    {
      key: 'closed',
      label: translate('caregiver.LoomCaregiverDetail.Workflows.Details.Closed'),
      value: invitation?.closedAt && formatDateTime(invitation.closedAt),
    },
  ];
};
